import { ProductUIConfig } from "@/config/productModeConfig.ts";
import debounce from "lodash/debounce";
import {
  Automation,
  automationQueries,
  updateAutomation,
} from "@/api/automations.ts";
import { Product } from "@/api/product.ts";
import React, { useCallback, useMemo } from "react";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Label } from "@/components/ui/label.tsx";
import { Slider } from "@/components/ui/slider.tsx";
import { toast } from "@/hooks/use-toast.ts";
import { cn } from "@/lib/utils.ts";

const fallbackConfig: ProductUIConfig = {
  showFrequency: true,
  showActions: true,
  showVolume: true,
  actionLabel: "Actions per Day",
  volumeLabel: "Estimated Daily Volume",
};

function getUIConfig(product: Product): ProductUIConfig {
  if (!product.automation_config) {
    return fallbackConfig;
  }

  return {
    showFrequency: product.automation_config.showFrequency ?? true,
    showActions: product.automation_config.showActions ?? true,
    showVolume: product.automation_config.showVolume ?? true,
    actionLabel: product.automation_config.actionLabel || "Actions per Day",
    volumeLabel:
      product.automation_config.volumeLabel || "Estimated Daily Volume",
  };
}

function getFrequencyCount(a: Automation | null, p: Product) {
  return getUIConfig(p).showActions && a
    ? (a.metadata?.frequency_count ?? 1)
    : 1;
}

function getEstimatedVolume(a: Automation | null, p: Product) {
  return getUIConfig(p).showVolume && a
    ? (a.metadata?.estimated_daily_volume ?? 1)
    : 1;
}

export function CapabilityConfig({
  className,
  personaId,
  automation,
  product,
}: {
  className?: string;
  personaId: string;
  automation: Automation;
  product: Product;
}) {
  const queryClient = useQueryClient();
  const uiConfig = getUIConfig(product);
  const freqCount = getFrequencyCount(automation, product);
  const docVolume = getEstimatedVolume(automation, product);
  const [localFreqCount, setLocalFreqCount] = React.useState(freqCount);
  const [localVolume, setLocalVolume] = React.useState(docVolume);

  const { data: generalConfig } = useSuspenseQuery(
    automationQueries.generalConfig(personaId),
  );

  const disableAll = !generalConfig;

  const save = useMemo(
    () => async (automationId: string, updates: Partial<Automation>) => {
      try {
        await updateAutomation(personaId, automationId, updates);
        queryClient.invalidateQueries(automationQueries.automations(personaId));
        queryClient.invalidateQueries(automationQueries.costs(personaId));
      } catch {
        toast({
          title: "Error",
          description: "Failed to save automation config",
          variant: "destructive",
        });
      }
    },
    [personaId, queryClient],
  );

  const handleConfigUpdate = useCallback(
    (automationId: string, updates: Partial<Automation>) => {
      // Local update
      queryClient.setQueryData(
        ["automations", personaId],
        (old: Automation[] | undefined) => {
          if (!old) return old;
          return old.map((item) => {
            if (item.automation_id !== automationId) return item;
            if (updates.metadata) {
              return {
                ...item,
                metadata: {
                  ...item.metadata,
                  ...updates.metadata,
                },
              };
            }
            return { ...item, ...updates };
          });
        },
      );
      // Debounced server
      save(automationId, updates);
    },
    [personaId, queryClient, save],
  );

  const debouncedUpdateFreqCount = React.useMemo(
    () =>
      debounce((val: number) => {
        handleConfigUpdate(automation.automation_id, {
          metadata: {
            ...automation.metadata,
            frequency_count: val,
          },
        });
      }, 500),
    [automation, handleConfigUpdate],
  );
  const debouncedUpdateVolume = React.useMemo(
    () =>
      debounce((val: number) => {
        handleConfigUpdate(automation.automation_id, {
          metadata: {
            ...automation.metadata,
            estimated_daily_volume: val,
          },
        });
      }, 500),
    [automation, handleConfigUpdate],
  );

  if (!uiConfig.showActions && !uiConfig.showVolume) {
    return null;
  }

  return (
    <div
      className={cn(
        "text-[#ABABAB] w-full flex flex-row gap-4 flex-wrap",
        className,
      )}
    >
      {uiConfig.showActions && (
        <div className="pt-2 flex-grow">
          <Label>{uiConfig.actionLabel}</Label>
          <Slider
            min={1}
            max={10}
            step={1}
            value={[localFreqCount]}
            onValueChange={([val]) => {
              setLocalFreqCount(val);
              if (!disableAll) {
                debouncedUpdateFreqCount(val);
              }
            }}
            disabled={disableAll}
            className="flex-1 mt-2"
          />
          <div className="mt-1">{localFreqCount}</div>
        </div>
      )}

      {uiConfig.showVolume && (
        <div className="pt-2 flex-grow">
          <Label>{uiConfig.volumeLabel}</Label>
          <Slider
            min={1}
            max={20}
            step={1}
            value={[localVolume]}
            onValueChange={([val]) => {
              setLocalVolume(val);
              debouncedUpdateVolume(val);
            }}
            disabled={disableAll}
            className="flex-1 mt-2"
          />
          <div className="mt-1">{localVolume}</div>
        </div>
      )}
    </div>
  );
}
